import RefreshIcon from '@/assets/icons/Refresh';
import { YEAR } from '@/common/constants';
import { Form } from '@/components/ui/form';
import { cn } from '@/lib/utils.ts';
import { Search, SlidersHorizontal } from 'lucide-react';
import { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputControl } from '../controls/input-control';
import SelectControl from '../controls/select-control';
import { Button } from '../ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import useFilter from './useFilter';

interface IFilterProps {
  page: string;
  className: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isMonitoring?: boolean;
  isLands?: boolean;
}

const Filter: FC<IFilterProps> = ({
  page,
  className,
  isOpen,
  setIsOpen,
  isMonitoring = false,
  isLands = false,
}) => {
  const { t } = useTranslation();
  const {
    user,
    form,
    control,
    regionList,
    districtList,
    handleForm,
    clearFilter,
    selectFormat,
    plantsList,
  } = useFilter({ page });
  const [size, setSize] = useState({
    width: window.innerWidth,
  });
  useEffect(() => {
    const handleResize = () => {
      setSize({
        width: window.innerWidth,
      });
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Fragment>
      <Form {...form}>
        <form onSubmit={handleForm} className={cn('', className)}>
          <div className="flex items-end justify-between gap-3">
            <Collapsible
              open={isOpen}
              onOpenChange={setIsOpen}
              className="w-full"
            >
              <div className="flex justify-between">
                <div
                  className={
                    size.width > 1850
                      ? 'flex items-end gap-3 w-[1000px]'
                      : 'flex items-end gap-3 w-[950px]'
                  }
                >
                  {!isLands && (
                    <SelectControl
                      control={control}
                      name="plant_id"
                      label={t('plant.type')}
                      options={plantsList || []}
                      className={cn('min-w-[200px] w-[200px]')}
                      selectClass={'h-8'}
                      labelClass={'text-xs'}
                      notRequired
                    />
                  )}

                  <SelectControl
                    className={cn('min-w-[100px] w-[100px]')}
                    control={form?.control}
                    selectClass={'h-8'}
                    name="year"
                    label={t('year')}
                    options={YEAR}
                    placeholder={t('year')}
                    notRequired
                  />

                  {!isMonitoring && !user?.region_id && (
                    <SelectControl
                      control={control}
                      name="region_id"
                      label={t('select.region')}
                      options={regionList ? regionList.map(selectFormat) : []}
                      className={
                        size.width > 1750
                          ? 'min-w-[200px] w-[200px]'
                          : 'min-w-[170px] w-[170px]'
                      }
                      selectClass={'h-8'}
                      labelClass={'text-xs'}
                      notRequired
                    />
                  )}
                  {!isMonitoring && !user?.district_id && (
                    <SelectControl
                      control={control}
                      name="district_id"
                      label={t('select.district')}
                      options={
                        districtList ? districtList.map(selectFormat) : []
                      }
                      className={cn(
                        'min-w-[200px] w-[200px]',
                        form.watch('region_id') ? '' : 'pointer-events-none',
                      )}
                      selectClass={'h-8'}
                      labelClass={'text-xs'}
                      notRequired
                    />
                  )}

                  <InputControl
                    control={form.control}
                    labelClass={'text-xs'}
                    name="inn"
                    inputClass="h-8"
                    label={t('inn')}
                    className="min-w-[140px] w-[200px]"
                    type="number"
                    maxLength={9}
                    withoutDescription
                  />

                  {!isMonitoring &&
                    ((user?.region_id && user?.district_id) ||
                      size.width > 2220) && (
                      <>
                        <InputControl
                          control={form.control}
                          labelClass={'text-xs'}
                          name="contour_number"
                          inputClass="h-8"
                          label={t('contour.number')}
                          className="min-w-[140px] w-[200px]"
                          withoutDescription
                        />
                        <InputControl
                          control={form.control}
                          labelClass={'text-xs'}
                          name="cadastre"
                          inputClass="h-8"
                          label={t('cadastral.number')}
                          className={
                            size.width > 1750
                              ? 'min-w-[200px] w-[200px]'
                              : 'min-w-[170px] w-[170px]'
                          }
                          withoutDescription
                        />
                      </>
                    )}
                </div>
                <div className="flex items-end justify-between space-x-4 ml-2">
                  <div className="flex gap-4">
                    <Button
                      type="submit"
                      variant="outline"
                      className={cn(
                        'p-[10px] active:translate-y-[1px] duration-200',
                      )}
                    >
                      <Search className="stroke-cyan-default" />
                    </Button>
                    <Button
                      type="button"
                      onClick={() => clearFilter()}
                      variant="outline"
                      className=" p-[10px] active:translate-y-[1px] duration-200"
                    >
                      <RefreshIcon />
                    </Button>
                  </div>
                  {!isMonitoring && (
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="p-2.5 active:translate-y-[1px] duration-200"
                      >
                        <SlidersHorizontal className="stroke-cyan-default w-5 h-5" />
                      </Button>
                    </CollapsibleTrigger>
                  )}
                </div>
              </div>
              <CollapsibleContent className="space-y-2 overflow-hidden transition-all data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp">
                <div className="flex items-end gap-3 mt-2 mb-[1px] ml-[1px]">
                  {!user?.region_id &&
                    !user?.district_id &&
                    size.width < 2220 && (
                      <>
                        <InputControl
                          control={form.control}
                          labelClass={'text-xs'}
                          name="contour_number"
                          inputClass="h-8"
                          label={t('contour.number')}
                          className="min-w-[140px] w-[200px]"
                          withoutDescription
                        />
                        <InputControl
                          control={form.control}
                          labelClass={'text-xs'}
                          name="cadastre"
                          inputClass="h-8"
                          label={t('cadastral.number')}
                          className={
                            size.width > 1850
                              ? 'min-w-[200px] w-[200px]'
                              : 'min-w-[170px] w-[170px]'
                          }
                          withoutDescription
                        />
                      </>
                    )}
                  <InputControl
                    className={
                      size.width > 1750
                        ? 'min-w-[200px] w-[200px]'
                        : 'min-w-[170px] w-[170px]'
                    }
                    control={form.control}
                    inputClass="h-8"
                    labelClass={'text-xs'}
                    name="farmer"
                    label={t('farmer')}
                    withoutDescription
                  />
                  <InputControl
                    control={form.control}
                    labelClass={'text-xs'}
                    name="reg_number"
                    inputClass="h-8"
                    label={t('lot.number')}
                    className={
                      size.width > 1850
                        ? 'min-w-[200px] w-[200px]'
                        : 'min-w-[170px] w-[170px]'
                    }
                    withoutDescription
                  />
                  <InputControl
                    control={form.control}
                    labelClass={'text-xs'}
                    name="area_from"
                    inputClass="h-8"
                    label={t('area.from')}
                    className={
                      size.width > 1850
                        ? 'min-w-[200px] w-[200px]'
                        : 'min-w-[170px] w-[170px]'
                    }
                    withoutDescription
                  />
                  <InputControl
                    control={form.control}
                    labelClass={'text-xs'}
                    name="area_to"
                    inputClass="h-8"
                    label={t('area.to')}
                    className={
                      size.width > 1850
                        ? 'min-w-[200px] w-[200px]'
                        : 'min-w-[170px] w-[170px]'
                    }
                    withoutDescription
                  />
                  {/* <InputControl
                    control={form.control}
                    labelClass={'text-xs'}
                    name="crop_id"
                    inputClass="h-8"
                    label={t('crop.id')}
                    className={
                      size.width > 1850
                        ? 'min-w-[200px] w-[200px]'
                        : 'min-w-[170px] w-[170px]'
                    }
                    withoutDescription
                  /> */}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </form>
      </Form>
    </Fragment>
  );
};

export default Filter;
